import React, { Fragment, useEffect, useState, useMemo, useCallback } from 'react'
import { useWatch, FormProvider, Controller, useForm, useFormContext } from 'react-hook-form'
import { offset, flip, shift } from '@floating-ui/react-dom-interactions'
import { yupResolver } from '@hookform/resolvers/yup'
import _ from 'lodash'
import { v4 } from 'uuid'
import cx from 'classnames'

// assets
import Settings from 'Images/bot-flow-settings.svg'

// others
import Textarea from 'react-textarea-autosize'
import ToggleSwitch from 'Src/components/Toggle'
import Checkbox from 'Design/components/Checkbox'
import Popover from 'Src/b/components/ReactPopover/ReactPopover'
import { cmeFormFieldValidationSchema } from './cme-form-validation-schema'

export const getPayload = (data, stepId) => {
  const type = 'collect_information'
  const action = { ...data.action, type: 'goToStep' }
  const fields =
    [...data.collectInfo].map((item) => {
      const { name, type, label, enabled, helpText, linkedTo, validations, options, maxFileCount } = item.field
      const field = {
        type,
        name: !_.isEmpty(name) ? name : null,
        label: !_.isEmpty(label) ? label : null,
        helpText: !_.isEmpty(helpText) ? helpText : null,
        enabled,
        validations: {
          required: validations.required
        }
      }

      if (type === 'dropdown') {
        const selectedFieldDefaultValue = !_.isEmpty(item.field.defaultValue) ? { ...item.field.defaultValue } : null
        const defaultValue = options.find((opt) => opt.default) ?? selectedFieldDefaultValue
        field.defaultValue = defaultValue ? { label: defaultValue.label, value: defaultValue.value } : null
        field.linkedTo = !_.isEmpty(linkedTo) ? linkedTo : null

        const optionsList = options.map((data) => {
          const opt = { ...data }
          return {
            id: opt.id,
            disabled: opt.disabled,
            label: opt.label,
            value: opt.value
          }
        })
        field.options = [...optionsList]
      } else if (type === 'attachments') {
        field.maxFileCount = maxFileCount
        field.validations = {
          ...validations
        }
      } else {
        field.linkedTo = !_.isEmpty(linkedTo) ? linkedTo : null
      }

      if (field.linkedTo) {
        field.linkedTo = field.linkedTo.includes('visitor.') ? field.linkedTo : `visitor.${field.linkedTo}`
      }

      return { ...field }
    }) ?? []

  return {
    stepId,
    setting: {
      type,
      action,
      fields
    }
  }
}

const Option = (props) => {
  const { onSelectOption, close, text } = props

  const handleClick = useCallback(() => {
    onSelectOption()
    close()
  }, [close, onSelectOption])

  return (
    <div className='menu-item' onClick={handleClick}>
      {text}
    </div>
  )
}

const OptionsMenu = (props) => {
  const { item, index, options, applyNewTheme } = props

  const { setValue, trigger, getValues } = useFormContext()

  const getMiddlewareConfig = useMemo(() => {
    return [offset(4), flip(), shift()]
  }, [])

  const setOptions = (list) => {
    setValue(`options`, [...list])
    trigger()
  }

  const handleSetDefaultValue = (item) => {
    const list = options.map((field) => {
      return { ...field, default: field.id === item.id }
    })

    setOptions(list)
  }

  const handleUnsetOption = () => {
    const list = options.map((field) => {
      return { ...field, default: false }
    })

    setOptions(list)
  }

  const handleDeleteOption = (item) => {
    const options = getValues(`options`)
    if (!options || !options.length) return

    const list = options.filter((field) => {
      return field.id !== item.id
    })

    setOptions(list)
  }

  const handleDisableOption = (index) => {
    const list = options.map((field, i) => {
      if (i !== index) {
        return field
      }

      return { ...field, disabled: true }
    })

    setOptions(list)
  }

  const handleEnableOption = (index) => {
    const list = options.map((field, i) => {
      if (i !== index) {
        return field
      }

      return { ...field, disabled: false }
    })

    setOptions(list)
  }

  return (
    <Popover
      placement='bottom-end'
      dismissConfig={{
        ancestorScroll: true,
        escapeKey: true,
        outsidePointerDown: true
      }}
      middleware={getMiddlewareConfig}
      render={({ close }) => (
        <div className={cx('options-menu', { 'new-design': applyNewTheme })}>
          {!item.default && (
            <Option text='Set as default' onSelectOption={() => handleSetDefaultValue(item)} close={close} />
          )}
          {item.default && <Option text='Unset' onSelectOption={() => handleUnsetOption(item)} close={close} />}
          {item.disabled && <Option text='Enable' onSelectOption={() => handleEnableOption(index)} close={close} />}
          {!item.disabled && <Option text='Disable' onSelectOption={() => handleDisableOption(index)} close={close} />}
          <Option text='Delete' onSelectOption={() => handleDeleteOption(item)} close={close} />
        </div>
      )}
    >
      <div className='options-dots' />
    </Popover>
  )
}

const AddOptionsTextarea = (props) => {
  const { options, errors, onCancel } = props

  const { register, setValue, trigger, getValues } = useFormContext()

  const onCancelAddOptions = () => {
    setValue(`inputOptions`, null, { shouldValidate: true })
    if (!_.isEmpty(errors)) {
      return
    }

    onCancel()
  }

  const onAddOptions = () => {
    const optionsString = getValues(`inputOptions`)?.replace(/\n/g, '__')?.trim() ?? ''
    if (_.isEmpty(optionsString)) {
      onCancelAddOptions()
      return
    }

    const list = optionsString.split('__') ?? []
    const newOptions = list.reduce((options, option) => {
      if (option && option.trim() !== '') {
        const hasDuplicateValue = options.find((opt) => option.toLowerCase() === opt.label.toLowerCase())
        if (!hasDuplicateValue) {
          options.push({ id: v4(), label: option, value: option, disabled: false, default: false })
        }
      }
      return options
    }, [])

    setValue(`options`, [...options, ...newOptions])
    trigger()
    onCancelAddOptions()
  }

  return (
    <Fragment>
      <Textarea
        maxRows={1}
        minRows={1}
        name='inputOptions'
        style={{ resize: 'none' }}
        {...register(`inputOptions`)}
        className={cx('form-control', {
          'is-invalid': errors.options || errors.inputOptions
        })}
      />
      {errors.options && <small className='form-text text-danger'>{errors.options?.message}</small>}
      {errors.inputOptions && <small className='form-text text-danger'>{errors.inputOptions?.message}</small>}
      <div className='primary-action-btn-group'>
        <button
          className='btn add'
          type='button'
          onClick={() => onAddOptions()}
          disabled={errors.options?.type === 'max' || errors.inputOptions?.type === 'unique'}
        >
          + Add Value
        </button>
        <button className='btn cancel' type='button' onClick={() => onCancelAddOptions()}>
          Cancel
        </button>
      </div>
    </Fragment>
  )
}

const DropdownOptions = (props) => {
  // props
  const { errors, optionList, applyNewTheme } = props
  // state
  const [showAddOptions, setShowAddOptions] = useState(false)

  const { control } = useFormContext()

  const options = useWatch({
    name: `options`,
    control,
    defaultValue: optionList
  })

  useEffect(() => {
    if (!options || !options?.length) {
      setShowAddOptions(true)
    }
  }, [options])

  const handleCancel = () => {
    setShowAddOptions(false)
  }

  return (
    <div className='form-group input-group'>
      <label className='text-label'>
        Dropdown Values
        {options && options.length > 0 && <span> ({options.length}) </span>}
      </label>
      {options && options.length > 0 && (
        <div className='options-container'>
          {options.map((item, index) => (
            <div className={cx('option-item')} key={index}>
              <div className={cx('option-label', { disabled: item.disabled })}>
                {_.upperFirst(item.label)}
                {item.default && <span className='default-batch'>Default</span>}
              </div>
              <OptionsMenu item={item} index={index} applyNewTheme={applyNewTheme} options={options} />
            </div>
          ))}
        </div>
      )}
      {!showAddOptions && (
        <div className='add-option-link' onClick={() => setShowAddOptions(true)}>
          Add Option
        </div>
      )}
      {showAddOptions && <AddOptionsTextarea errors={errors} options={options} onCancel={handleCancel} />}
    </div>
  )
}

const getDefaultValues = (data) => {
  const field = { ...data }
  const validations = { ...field.validations }
  if (field.type === 'dropdown') {
    const options = _.get(field, 'options', [])
    field.options = options.map((option) => {
      return { ...option, default: option.value === field.defaultValue?.value }
    })
  }

  return {
    ...field,
    validations: {
      ...validations
    }
  }
}

const FILE_TYPE_INITIAL_STATE = [
  {
    id: 1,
    label: 'Documents',
    value: 'documents',
    allowedFormats: ['pdf', 'doc', 'docx', 'ppt', 'xls', 'xlsx'],
    checked: false
  },
  {
    id: 2,
    label: 'Images',
    value: 'images',
    allowedFormats: ['png', 'jpg', 'jpeg', 'svg', 'tiff', 'webp'],
    checked: false
  },
  {
    id: 3,
    label: 'Audio / Video',
    value: 'audio / video',
    allowedFormats: ['mp3', 'mov', 'avi', 'mp4'],
    checked: false
  }
]

const EditFieldInfo = (props) => {
  // props
  const { close, arrow, collectInfo, fieldErrors, applyNewTheme, index, onDeleteField, onUpdateField, onCancelField } =
    props
  const { field, id, deletable = true } = props.item
  const defaultValues = getDefaultValues(field)
  // form
  const methods = useForm({
    defaultValues,
    mode: 'all',
    reValidateMode: 'onChange',
    shouldFocusError: true,
    shouldUnregister: false,
    context: {
      collectInfo,
      selectedItem: props.item
    },
    resolver: yupResolver(cmeFormFieldValidationSchema)
  })
  const {
    control,
    watch,
    register,
    reset,
    setValue,
    trigger,
    clearErrors,
    getValues,
    formState: { errors }
  } = methods

  const fileTypeCategories = watch('validations.allowedTypeCategories')

  const [fileTypes, setFileTypes] = useState(FILE_TYPE_INITIAL_STATE)

  useEffect(() => {
    if (fileTypeCategories && fileTypeCategories.length) {
      const list = FILE_TYPE_INITIAL_STATE.map((type) => {
        return {
          ...type,
          checked: fileTypeCategories.includes(type.label)
        }
      })
      setFileTypes(list)
    }
  }, [fileTypeCategories, setFileTypes])

  const onSave = useCallback(() => {
    if (!_.isEmpty(errors)) {
      return
    }

    let defaultValue
    const data = _.cloneDeep(getValues())
    const item = { ...props.item }
    const field = { ...data }
    const validations = { ...field.validations }
    const allowedTypeCategories = []

    if (data.type === 'dropdown') {
      const defaultField = data.options.find((opt) => opt.default)
      defaultValue = defaultField ? { label: defaultField.label, value: defaultField.value } : null
    } else {
      const fileTypeCategories = fileTypes
      fileTypeCategories.forEach((type) => {
        if (type.checked) {
          allowedTypeCategories.push(type.label)
        }
      })
    }

    const payload = {
      ...item,
      field: {
        ...field,
        ...data,
        defaultValue: !_.isEmpty(defaultValue) ? { ...defaultValue } : null,
        validations: { ...validations, allowedTypeCategories }
      }
    }

    onUpdateField(payload, index)
    close()
  }, [close, errors, fileTypes, getValues, index, onUpdateField, props.item])

  const fileTypeError = useMemo(() => {
    if (!fieldErrors || !fieldErrors.collectInfo) {
      return null
    }

    const err = fieldErrors.collectInfo[index]?.field?.validations?.allowedTypeCategories ?? null
    return err
  }, [fieldErrors, index])

  const onCancel = useCallback(() => {
    reset()
    clearErrors()
    onCancelField()
    close()
  }, [clearErrors, close, onCancelField, reset])

  const onDelete = useCallback(() => {
    onDeleteField(index)
    clearErrors()
    close()
  }, [clearErrors, close, index, onDeleteField])

  const updateFormValue = (key, val) => {
    trigger()
    const value = val?.trim() ?? ''
    setValue(key, value)
  }

  const onChangeFileType = (item, isChecked) => {
    const list = fileTypes.map((type) => {
      return {
        ...type,
        checked: type.id === item.id ? !isChecked : type.checked
      }
    })
    setFileTypes(list)
  }

  return (
    <FormProvider {...methods}>
      <div className={cx('ci-popover-container', { 'new-design': applyNewTheme })} id={id}>
        <div className='ci-popover-header'>
          <div className='icon'>
            <Settings className='settings-icon' height='13' width='13' />
          </div>
          <div className='header-title'> {_.upperFirst(defaultValues?.name)} </div>
        </div>
        <div className='ci-popover-content'>
          <div className='form-group input-group'>
            <label className='text-label'> Label </label>
            <input
              type='text'
              placeholder=''
              autoFocus
              defaultValue={defaultValues?.label}
              {...register(`label`)}
              className={cx('form-control', 'input-text', {
                'is-invalid': errors.label
              })}
              onBlur={(e) => updateFormValue('label', _.upperFirst(e.target.value))}
            />
            {errors.label && <small className='form-text text-danger'>{errors.label?.message}</small>}
          </div>
          {(_.lowerCase(defaultValues?.name) === 'dropdown' ||
            _.lowerCase(defaultValues?.name) === 'auto complete') && (
            <DropdownOptions errors={errors} optionList={defaultValues?.options} applyNewTheme={applyNewTheme} />
          )}
          <div className='form-group input-group'>
            <label className='text-label'> Help Text </label>
            <input
              type='text'
              {...register(`helpText`)}
              defaultValue={defaultValues?.helpText}
              className={cx('form-control', 'input-text', {
                'is-invalid': errors.helpText
              })}
              onBlur={(e) => updateFormValue('helpText', e.target.value)}
            />
            {errors.helpText && <small className='form-text text-danger'>{errors.helpText?.message}</small>}
          </div>
          {_.lowerCase(defaultValues?.name) !== 'name' &&
            _.lowerCase(defaultValues?.name) !== 'email' &&
            _.lowerCase(defaultValues?.type) !== 'attachments' && (
              <div className='form-group input-group'>
                <label className='text-label'> Link value to a visitor property </label>
                <input
                  type='text'
                  {...register(`linkedTo`)}
                  defaultValue={defaultValues?.linkedTo}
                  className={cx('form-control', 'input-text', {
                    'is-invalid': errors.linkedTo
                  })}
                  onBlur={(e) => updateFormValue('linkedTo', e.target.value)}
                />
                {errors.linkedTo && <small className='form-text text-danger'>{errors.linkedTo?.message}</small>}
              </div>
            )}
          {_.lowerCase(field?.type) === 'attachments' && (
            <Fragment>
              <div className='form-group input-group'>
                <label className='text-label'> File Types </label>
                <div className='file-type-group'>
                  {fileTypes.map((item, index) => (
                    <Fragment key={item.id}>
                      <div className='file-type-container'>
                        <div className='file-type-wrapper'>
                          <Checkbox
                            id={item.value !== 'audio / video' ? `${item.value}` : `audio-video`}
                            className={cx('file-type-checkbox', {
                              'is-invalid': fileTypeError
                            })}
                            onChange={() => onChangeFileType(item, item.checked)}
                            checked={item.checked}
                          />
                          <label
                            className='file-type-label'
                            htmlFor={item.value !== 'audio / video' ? `${item.value}` : `audio-video`}
                          >
                            {item.label}
                          </label>
                        </div>
                        <div className='file-type-info-wrapper'>
                          <small className='form-text' style={{ margin: 0 }}>
                            <div className='file-type-info'>{item.allowedFormats.join(', ')}</div>
                          </small>
                        </div>
                      </div>
                    </Fragment>
                  ))}
                  {fileTypeError && <small className='form-text text-danger'>{fileTypeError?.message}</small>}
                </div>
              </div>
            </Fragment>
          )}
          <div className='form-group input-switch-group'>
            <Controller
              name='validations.required'
              control={control}
              render={({ field: { value, onChange } }) => {
                const handleToggleChange = () => {
                  onChange(!value)
                  setValue('validations.required', !value)
                  trigger()
                }

                return <ToggleSwitch checked={value} onClick={handleToggleChange} />
              }}
            />
            <label htmlFor='mandatoryField' className='label'>
              Mandatory Field
            </label>
          </div>
        </div>
        <div className='ci-popover-footer'>
          <div className='action-btn-group'>
            <div className='primary-action-btn-group'>
              <button className='button-primary small save' type='button' onClick={() => onSave()}>
                Save
              </button>
              <button className='btn cancel' type='button' onClick={() => onCancel()}>
                Cancel
              </button>
            </div>
            {deletable && (
              <button className='btn delete' type='button' onClick={() => onDelete()}>
                Delete
              </button>
            )}
          </div>
        </div>
        {arrow}
      </div>
    </FormProvider>
  )
}

export default EditFieldInfo
